import React from "react";
import BlocksSelector from "./BlocksSelector";
import { useDispatch } from "react-redux";
import { setBlockRange } from "../../store/state/BlockSuggestion";
import { Clock, X } from "lucide-react";
import { ReactComponent as SelectBlock } from "../../icons/Select_block.svg";

export const TimeDivider = [
  {
    name: "Off-Peak",
    range: "12:00 AM – 6:00 AM",
    startBlock: 1,
    endBlock: 24,
  },
  { name: "Daytime", range: "6:00 AM – 6:00 PM", startBlock: 25, endBlock: 72 },
  {
    name: "Evening + Late Night",
    range: "6:00 PM – 12:00 AM",
    startBlock: 73,
    endBlock: 96,
  },
];
 
export const BlockSuggestions = [
  {
    label: "Morning Peak",
    range: "6:00 AM – 10:00 AM",
    startBlock: 25,
    endBlock: 40,
  },
  {
    label: "Midday Dip",
    range: "12:00 PM – 2:00 PM",
    startBlock: 49,
    endBlock: 56,
  },
  {
    label: "Evening Peak",
    range: "6:00 PM – 10:00 PM",
    startBlock: 73,
    endBlock: 88,
  },
  {
    label: "Off-Peak Night",
    range: "2:00 AM – 6:00 AM",
    startBlock: 9,
    endBlock: 24,
  },
];

type BlockRangeSelectorProps = {
  blocks: number[];
  placeholder:string;
  selectedOptions: number[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<number[]>>;
};

const BlockRangeSelector: React.FC<BlockRangeSelectorProps> = ({
  blocks,
  placeholder,
  selectedOptions,
  setSelectedOptions,
}) => {
  const [open, setOpen] = React.useState(false);
  const [finalRange, setFinalRange] = React.useState<Record<string, unknown>>(
    {}
  );

  // CRITICAL FIX: Sync finalRange when selectedOptions changes externally
  React.useEffect(() => {
    if (selectedOptions.length === 0) {
      setFinalRange({});
    } else {
      // When blocks are removed externally, recalculate finalRange
      const newFinalRange: Record<string, number[]> = {};
      
      // Group consecutive blocks into ranges
      if (selectedOptions.length > 0) {
        const sortedBlocks = [...selectedOptions].sort((a, b) => a - b);
        let rangeStart = sortedBlocks[0];
        let rangeEnd = sortedBlocks[0];
        let currentRange = [sortedBlocks[0]];

        for (let i = 1; i < sortedBlocks.length; i++) {
          if (sortedBlocks[i] === rangeEnd + 1) {
            // Consecutive block, extend the range
            rangeEnd = sortedBlocks[i];
            currentRange.push(sortedBlocks[i]);
          } else {
            // Non-consecutive, save current range and start new one
            const rangeKey = `${rangeStart}-${rangeEnd}`;
            newFinalRange[rangeKey] = currentRange;
            
            rangeStart = sortedBlocks[i];
            rangeEnd = sortedBlocks[i];
            currentRange = [sortedBlocks[i]];
          }
        }
        
        // Save the last range
        const rangeKey = `${rangeStart}-${rangeEnd}`;
        newFinalRange[rangeKey] = currentRange;
      }
      
      setFinalRange(newFinalRange);
    }
  }, [selectedOptions]);

  const removeTag = (blockToRemove: number, indexToRemove: number) => {
    setSelectedOptions((prev) => {
      const newArray = prev.filter((_, index) => index !== indexToRemove);
      return newArray;
    });
  };

  const clearAllBlocks = () => {
    setSelectedOptions([]);
  };

  return (
    <div className="relative">
      <div
        className={`p-2 border border-[#e2e2e5] flex items-center justify-between gap-0 font-medium rounded-lg ${
          selectedOptions.length > 0
            ? "w-[175px] h-[40px]"
            : "w-[175px] h-[38px]"
        }`}
      >
        <div
          className="flex items-start gap-1 flex-1 overflow-y-auto min-h-5 max-h-[38px] scroll-thin cursor-pointer"
          onClick={() => setOpen(!open)}
        >
          {selectedOptions.length > 0 ? (
            <div className="flex flex-wrap gap-1 w-full">
              {selectedOptions.map((block, index) => (
                <div
                  key={`${block}-${index}`}
                  className="flex items-center gap-0 bg-gray-200 text-gray-700 text-xs px-1 py-1 rounded whitespace-nowrap flex-shrink-0"
                >
                  <span>{block}</span>
                  <button
                    type="button"
                    className="cursor-pointer hover:bg-red-400 hover:text-white rounded text-gray-500 text-sm leading-none px-1 transition-colors flex-shrink-0"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeTag(block, index);
                    }}
                    style={{ minWidth: "16px", minHeight: "16px" }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-sm px-2 py-2 text-[#8c8a96]">
              {placeholder}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {selectedOptions.length > 0 && (
            <X
              size={18}
              className="text-[#A8A29F] cursor-pointer hover:text-red-600 transition-colors"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearAllBlocks();
              }}
            />
          )}
          <SelectBlock
            className="w-[26px] h-[26px] cursor-pointer"
            onClick={(e: React.MouseEvent<SVGSVGElement>) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(!open);
            }}
          />
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[9999] bg-[#37393c]/60 backdrop-blur-sm flex items-center justify-center">
          <div onClick={(e) => e.stopPropagation()}>
            <BlockSelectionModel
              setSelectedOptions={setSelectedOptions}
              selectedOptions={selectedOptions}
              blocks={blocks}
              setOpen={setOpen}
              setFinalRange={setFinalRange}
              finalRange={finalRange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

type BlockSelectionModelProps = {
  blocks: number[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<number[]>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedOptions: number[];
  finalRange: Record<string, unknown>;
  setFinalRange: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
};

function BlockSelectionModel({
  blocks,
  setSelectedOptions,
  setOpen,
  selectedOptions,
  finalRange,
  setFinalRange,
}: BlockSelectionModelProps) {
  const dispatch = useDispatch();

  return (
    <div className="transition-all border ease-in-out duration-200 w-[800px] text-sm text-[#A8A29F] h-full bg-[#FAFAFA] rounded-xl shadow-lg relative">
      <div className="flex h-full w-full">
        <div className="p-5 h-full rounded-lg w-[35%] bg-gray-50">
          <ul className="mt-2 h-full flex flex-col gap-4">
            <h5 className="text-gray-800 text-xs font-medium">
              Suggested Blocks
            </h5>
            {BlockSuggestions.map((suggestion, index) => (
              <li
                key={index}
                onClick={() => {
                  const range: number[] = [];
                  for (
                    let i = suggestion.startBlock;
                    i <= suggestion.endBlock;
                    i++
                  ) {
                    range.push(i);
                  }
                  // setSelectedOptions((prev) => [...prev, ...range]);

                  dispatch(
                    setBlockRange({
                      startBlock: suggestion.startBlock,
                      endBlock: suggestion.endBlock,
                      isClicked: true,
                    })
                  );
                }}
                onMouseEnter={() => {
                  dispatch(
                    setBlockRange({
                      startBlock: suggestion.startBlock,
                      endBlock: suggestion.endBlock,
                    })
                  );
                }}
                onMouseLeave={() => {
                  dispatch(setBlockRange({ startBlock: null, endBlock: null }));
                }}
                className="text-xs cursor-pointer"
              >
                <button
                  type="button"
                  className="text-start p-2 w-full hover:border-primary border border-transparent rounded-lg hover:text-primary hover:bg-primary/5 transition-colors ease-in-out duration-200"
                >
                  {suggestion.label}
                  <br />
                  <span>{suggestion.range}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="w-[65%] h-full bg-white rounded-xl shadow-lg">
          <BlocksSelector
            key={JSON.stringify(finalRange)}
            setOpen={setOpen}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            blocks={blocks}
            finalRange={finalRange}
            setFinalRange={setFinalRange}
          />
        </div>
      </div>
    </div>
  );
}
 
export default BlockRangeSelector;